import { format, parse } from "date-fns";
import { fromCallback, fromPromise } from "xstate";
import type { EventObject } from "xstate";

import { Application } from "@/lib/__generated/api";
import type { SlotsSearchResponseV4, SlotsSearchRequestV6 } from "@/lib/__generated/api";
import { fetchNearbySlots } from "@/lib/services/getNearbySlots";
import { fetchSlots } from "@/lib/services/getSlots";

export type SlotsSearchInput = SlotsSearchRequestV6 & {
  questionnaireId?: string;
  restUrl: string;
};

function toSearchParams(input: SlotsSearchInput): Record<string, unknown> {
  const dateOfBirth = input.dateOfBirth
    ? format(parse(input.dateOfBirth, "MM/dd/yyyy", new Date()), "yyyy-MM-dd")
    : undefined;

  return {
    startDate: input.startDate,
    endDate: input.endDate,
    facilityCode: input.facilityCode,
    application: Application.AspenDental,
    digitalVisitReason: input.digitalVisitReason,
    ...(input.firstName && { firstName: input.firstName }),
    ...(input.lastName && { lastName: input.lastName }),
    ...(dateOfBirth && { dateOfBirth }),
    ...(input.email && { email: input.email }),
    ...(input.phone && { phone: input.phone }),
    ...(input.questionnaireId && { questionnaireId: input.questionnaireId }),
    ...(input.questionnaireAnsweredQuestions && {
      questionnaireAnsweredQuestions: input.questionnaireAnsweredQuestions,
    }),
  };
}

export const fetchSlotsActor = fromPromise<SlotsSearchResponseV4[], SlotsSearchInput>(
  async ({ input }): Promise<SlotsSearchResponseV4[]> => {
    try {
      return await fetchSlots(toSearchParams(input), input.restUrl);
    } catch (error) {
      throw new Error(error instanceof Error ? error.message : "Failed to fetch slots");
    }
  },
);

// Reports back to its parent instead of resolving, so the spawned child settles into a named
// scheduler event rather than an `xstate.done.actor.*` one.
export const fetchNearbySlotsActor = fromCallback<EventObject, SlotsSearchInput>(
  ({ input, sendBack }) => {
    void fetchNearbySlots(toSearchParams(input), input.restUrl).then(
      (nearbySlots) => sendBack({ type: "NEARBY_SLOTS_LOADED", nearbySlots }),
      () => sendBack({ type: "NEARBY_SLOTS_FAILED" }),
    );
  },
);
